import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {StartupService} from '../../service-ui/startup.service';
import {EcommerceConnectionService, ShopifyLinkResponse} from '../../service-data/ecommerce-connection.service';
import {parseServerError} from '../../service-data/http-error';

/**
 * Binds an installed Shopify store to a Snowfall Press organization.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS PAGE EXISTS AT ALL
 * ---------------------------------------------------------------------------
 * The Shopify install itself does **not** create an `EcommerceConnection`.
 * Neither does opening the embedded app, nor `POST /shopify/session`, nor
 * signing up for a Snowfall Press account. Exactly one call creates it --
 * `POST /1.0/ui/ecommerceconnection/shopify/link` -- and this page is the only
 * thing that makes it.
 *
 * Until this component existed, a merchant could complete a perfect install
 * (offline token stored, all ten scopes granted, embedded app rendering) and
 * end up with **nothing connected**, because the link URL Shopify sent them to
 * fell through to the SPA shell and routed nowhere. The install row sat at
 * `PENDING` forever. See `docs/contracts/shared/ShopifyAppInstall.md` and
 * `inbox/_processed/2026-10-22e-...`.
 *
 * ---------------------------------------------------------------------------
 * THE NONCE IS A BEARER CREDENTIAL FOR A STORE
 * ---------------------------------------------------------------------------
 * Whoever holds it can bind that shop to their organization. Three
 * consequences, all deliberate:
 *
 * 1. **It is never written to `localStorage` or `sessionStorage`.** It is held
 *    in memory on `StartupService`, exactly as the legacy install's OAuth
 *    `code` is. This app's sign-in is an in-page Cognito call rather than a
 *    hosted-UI redirect, so the SPA is never torn down between arriving here
 *    and coming back from login -- in-memory survives the round trip, and
 *    nothing is left behind on a shared machine if it does not.
 * 2. **It is stripped from the address bar** by `AppComponent` as soon as it
 *    is captured, so it does not sit in browser history, in a bookmark, or in
 *    a `Referer` header on the next outbound request.
 * 3. **It is never logged.** The console lines below say whether a link
 *    succeeded, never what the nonce was.
 *
 * ---------------------------------------------------------------------------
 * THE ROUND TRIP, AND WHY `armReturn()` IS CALLED EVERY TIME
 * ---------------------------------------------------------------------------
 * A merchant arriving from Shopify is usually logged out, and frequently has
 * not selected the `organization` role. Both detours leave this page and come
 * back through `StartupComponent`, which consumes
 * `StartupService.pendingPostStartupRoute` **and clears it**. So the override
 * has to be re-armed before every outbound navigation; arming it once on entry
 * would survive the first detour and lose the second -- which is exactly the
 * path a merchant who must both sign in *and* switch role would take.
 */
@Component({
  selector: 'app-shopify-link',
  templateUrl: './shopify-link.component.html',
  styleUrls: ['./shopify-link.component.css']
})
export class ShopifyLinkComponent implements OnInit, OnDestroy {

  /**
   * - `INITIAL`      working out what is needed
   * - `NEEDS_NONCE`  arrived without one (direct visit, or a reload)
   * - `NEEDS_LOGIN`  not signed in; redirecting
   * - `NEEDS_ORG`    signed in, but not acting as an `organization`
   * - `LINKING`      request in flight
   * - `LINKED`       done
   * - `EXPIRED`      nonce past its TTL or already used -- recoverable
   * - `CONFLICT`     already redeemed, or shop bound to another organization
   * - `FAILED`       anything else
   */
  state: 'INITIAL' | 'NEEDS_NONCE' | 'NEEDS_LOGIN' | 'NEEDS_ORG'
       | 'LINKING' | 'LINKED' | 'EXPIRED' | 'CONFLICT' | 'FAILED' = 'INITIAL';

  /** The server's own sentence, when it sent one. Rendered as-is. */
  serverMessage = '';

  result: ShopifyLinkResponse = null;

  private onDestroySubject: Subject<boolean> = new Subject();

  constructor(public router: Router,
              public route: ActivatedRoute,
              public startupService: StartupService,
              public ecommerceConnectionService: EcommerceConnectionService) {}

  ngOnInit(): void {
    // `AppComponent` normally captures the nonce and strips it from the URL
    // before the router ever gets here. The query-param read is the fallback
    // for an in-app navigation that never passed through bootstrap.
    if ( !this.startupService.pendingShopifyLinkNonce ) {
      const fromUrl = this.route.snapshot.queryParamMap.get('nonce');
      if ( fromUrl ) {
        this.startupService.pendingShopifyLinkNonce = fromUrl;
      }
    }
    this.advance();
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  get shop(): string {
    return this.result && this.result.shop ? this.result.shop : '';
  }

  /**
   * Work out the next step and take it. Safe to call repeatedly -- it is the
   * single entry point used by `ngOnInit`, the retry button, and the return
   * from a role switch.
   */
  advance(): void {
    const nonce = this.startupService.pendingShopifyLinkNonce;
    if ( !nonce ) {
      this.state = 'NEEDS_NONCE';
      return;
    }

    if ( !this.startupService.getJWTToken() ) {
      this.state = 'NEEDS_LOGIN';
      this.armReturn();
      this.router.navigate(['/home/login']);
      return;
    }

    if ( this.startupService.selectedRole !== 'organization' ) {
      // The server answers 403 for every other role type. Asking first turns
      // a dead error into an action the merchant can actually take.
      this.state = 'NEEDS_ORG';
      return;
    }

    this.link(nonce);
  }

  /** Send the merchant to the role picker, armed to come back here. */
  chooseOrganizationRole(): void {
    this.armReturn();
    this.router.navigate(['/secure/selectRole']);
  }

  signIn(): void {
    this.armReturn();
    this.router.navigate(['/home/login']);
  }

  private armReturn(): void {
    this.startupService.pendingPostStartupRoute = ['/shopify/link'];
  }

  private link(nonce: string): void {
    this.state = 'LINKING';
    this.serverMessage = '';
    console.log('ShopifyLinkComponent: redeeming install link');
    this.ecommerceConnectionService.shopifyLink({ nonce: nonce })
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(
        response => {
          // Burn it locally the moment the server accepts it. The nonce is
          // single-use server-side anyway; keeping a spent credential in
          // memory buys nothing, and a second attempt could only produce 409.
          this.startupService.pendingShopifyLinkNonce = null;
          this.result = response;
          this.state = 'LINKED';
          // `warnings` and `setupStatus` are OPERATOR diagnostics and are
          // deliberately logged rather than rendered. They name server
          // environment variables and one variant embeds a raw exception
          // message, so showing them unreviewed would put our internals in
          // front of a merchant at the exact moment we are telling them
          // everything worked. Both fields stay plumbed: if a warning a
          // merchant must act on is ever added it will arrive with a machine
          // code and a contract change, never as prose in this array.
          // See inbox/_processed/2026-10-22h-...
          console.log('ShopifyLinkComponent: linked', {
            shop: response && response.shop,
            ecommerceConnectionId: response && response.ecommerceConnectionId,
            orgId: response && response.orgId,
            created: response && response.created,
            setupStatus: response && response.setupStatus,
            warnings: response && response.warnings
          });
        },
        error => this.handleFailure(error)
      );
  }

  /**
   * Map the five documented statuses onto five different things to do.
   *
   * One generic failure would be the wrong answer here: an expired nonce is
   * fixed by reopening the app and costs ten seconds, while a shop bound to
   * another organization cannot be fixed by the merchant at all. Telling those
   * apart is most of this screen's value on the day it goes wrong.
   */
  private handleFailure(error: any): void {
    const info = parseServerError(error);
    this.serverMessage = info.details || '';
    console.error('ShopifyLinkComponent: link failed', {
      httpStatus: info.httpStatus,
      details: info.details
    });

    switch ( info.httpStatus ) {
      case 400:
        // No nonce reached the server -- treat it as not having one.
        this.startupService.pendingShopifyLinkNonce = null;
        this.state = 'NEEDS_NONCE';
        return;
      case 403:
        // Role changed underneath us, or was never right.
        this.state = 'NEEDS_ORG';
        return;
      case 404:
      case 410:
        // Unknown nonce, or past its ten-minute TTL. Both recover the same
        // way: reopen the app in Shopify to mint a fresh link.
        this.startupService.pendingShopifyLinkNonce = null;
        this.state = 'EXPIRED';
        return;
      case 409:
        // Two distinct causes share this status -- already redeemed, and shop
        // bound to a DIFFERENT organization. Only the server's sentence tells
        // them apart, so it is rendered prominently and both next steps are
        // offered rather than pattern-matching on English prose.
        this.startupService.pendingShopifyLinkNonce = null;
        this.state = 'CONFLICT';
        return;
      default:
        this.state = 'FAILED';
        return;
    }
  }
}
